// src/controlsil/panelReporteSil.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import GridLayout from "react-grid-layout/legacy";
import { Global } from "../helpers/Global";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

// ✅ MUI
import { Box, Paper, Typography, Stack, Button } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

// ✅ Icons
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";

export const PanelReportesSIL = () => {
  // =========================
  // Validez reportes
  // =========================
  const TTL_BASE_HORAS = 32;
  const TTL_VIERNES_HORAS = 80;
  const TTL_SABADO_HORAS = 66;
  const TTL_DOMINGO_HORAS = 48;
  const HOUR_MS = 60 * 60 * 1000;

  // Layout fijo
  const W_FIJO = 1;
  const H_FIJO = 2;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [configSIL, setConfigSIL] = useState({
    cols_canvas: 120,
    filas_canvas: 40,
    col_px: 50,
    row_height: 60,
  });

  const [nodos, setNodos] = useState([]);
  const [ultimosReportes, setUltimosReportes] = useState([]);

  // 🆕 Ausencias activas
  const [ausenciasActivas, setAusenciasActivas] = useState([]);
  const [ausenciasLoading, setAusenciasLoading] = useState(false);

  // Scroll sync
  const tableroRef = useRef(null);
  const scrollTopRef = useRef(null);
  const syncingScrollRef = useRef(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  // =========================
  // Dimensiones lienzo
  // =========================
  const COLS = Math.max(1, Number(configSIL.cols_canvas || 120));
  const FILAS = Math.max(1, Number(configSIL.filas_canvas || 40));
  const COL_PX = Math.max(20, Number(configSIL.col_px || 50));
  const ROW_HEIGHT = Math.max(20, Number(configSIL.row_height || 60));

  const GRID_WIDTH = COLS * COL_PX;
  const GRID_HEIGHT = FILAS * ROW_HEIGHT;

  // =========================
  // Drag-to-pan (click izquierdo)
  // =========================
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
    moved: false,
  });

  const onPanMouseDown = (e) => {
    if (e.button !== 0) return;

    const tag = (e.target?.tagName || "").toLowerCase();
    if (["input", "textarea", "select", "button", "a"].includes(tag)) return;

    const el = tableroRef.current;
    if (!el) return;

    panRef.current.active = true;
    panRef.current.moved = false;
    panRef.current.startX = e.clientX;
    panRef.current.startY = e.clientY;
    panRef.current.startScrollLeft = el.scrollLeft;
    panRef.current.startScrollTop = el.scrollTop;

    setIsPanning(true);
  };

  const onPanMouseMove = (e) => {
    if (!panRef.current.active) return;
    const el = tableroRef.current;
    if (!el) return;

    const dx = e.clientX - panRef.current.startX;
    const dy = e.clientY - panRef.current.startY;

    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) panRef.current.moved = true;

    el.scrollLeft = panRef.current.startScrollLeft - dx;
    el.scrollTop = panRef.current.startScrollTop - dy;
  };

  const stopPan = () => {
    if (!panRef.current.active) return;
    panRef.current.active = false;
    setIsPanning(false);
  };

  // =========================
  // Fetch
  // =========================
  const fetchConfigSIL = async () => {
    const res = await fetch(`${Global.url}nodos/config`, { headers: authHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error("config");
    setConfigSIL({
      cols_canvas: Number(data.cols_canvas || 120),
      filas_canvas: Number(data.filas_canvas || 40),
      col_px: Number(data.col_px || 50),
      row_height: Number(data.row_height || 60),
    });
  };

  const fetchOrganigrama = async () => {
    const res = await fetch(`${Global.url}nodos/organigrama`, { headers: authHeaders() });
    const data = await res.json().catch(() => []);
    if (!res.ok) throw new Error("organigrama");

    const lista = Array.isArray(data) ? data : [];
    setNodos(
      lista.map((n) => ({
        ...n,
        id: Number(n.id),
        x: n.x ?? null,
        y: n.y ?? null,
        w: W_FIJO,
        h: H_FIJO,
        enPanel: n.x !== null && n.x !== undefined && n.y !== null && n.y !== undefined,
      }))
    );
  };

  const fetchUltimosReportes = async () => {
    const res = await fetch(`${Global.url}reportes/ultimo`, { headers: authHeaders() });
    const data = await res.json().catch(() => []);
    if (!res.ok) throw new Error("reportes");
    setUltimosReportes(Array.isArray(data) ? data : []);
  };

  const fetchAusenciasActivas = async () => {
    setAusenciasLoading(true);
    try {
      const res = await fetch(`${Global.url}nodos/ausencias/activas`, { headers: authHeaders() });
      const data = await res.json().catch(() => []);
      if (res.ok) setAusenciasActivas(Array.isArray(data) ? data : []);
    } finally {
      setAusenciasLoading(false);
    }
  };

  const recargar = async () => {
    setError("");
    try {
      await Promise.all([fetchConfigSIL(), fetchOrganigrama(), fetchUltimosReportes(), fetchAusenciasActivas()]);
    } catch (e) {
      console.error(e);
      setError("❌ No se pudieron recargar los datos del panel.");
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        await Promise.all([fetchConfigSIL(), fetchOrganigrama(), fetchUltimosReportes(), fetchAusenciasActivas()]);
      } catch (e) {
        console.error(e);
        setError("❌ No se pudieron cargar los datos del panel.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      fetchUltimosReportes().catch(() => {});
      fetchAusenciasActivas().catch(() => {});
    }, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  // =========================
  // Helpers
  // =========================
  const reportesMap = useMemo(() => {
    const m = new Map();
    (ultimosReportes || []).forEach((r) => m.set(Number(r.nodo_id), r));
    return m;
  }, [ultimosReportes]);

  const ausenciasMap = useMemo(() => {
    const m = new Map();
    (ausenciasActivas || []).forEach((a) => m.set(Number(a.nodo_id), a));
    return m;
  }, [ausenciasActivas]);

  const isNodoAusente = (nodoId) => ausenciasMap.has(Number(nodoId));

  const nodosPanel = useMemo(() => nodos.filter((n) => n.enPanel), [nodos]);

  const layoutPanel = useMemo(
    () =>
      nodosPanel.map((n) => ({
        i: String(n.id),
        x: Number(n.x ?? 0),
        y: Number(n.y ?? 0),
        w: W_FIJO,
        h: H_FIJO,
        static: true,
      })),
    [nodosPanel]
  );

  const ttlHorasSegunDia = (ms) => {
    const d = new Date(ms).getDay();
    if (d === 5) return TTL_VIERNES_HORAS;
    if (d === 6) return TTL_SABADO_HORAS;
    if (d === 0) return TTL_DOMINGO_HORAS;
    return TTL_BASE_HORAS;
  };

  const getEstadoVigente = (rep) => {
    if (!rep?.creado_en) return { vigente: false, estado: null, expiraMs: null };

    const creadoMs = new Date(rep.creado_en).getTime();
    if (!Number.isFinite(creadoMs)) return { vigente: false, estado: null, expiraMs: null };

    const expiraMs = creadoMs + ttlHorasSegunDia(creadoMs) * HOUR_MS;
    const vigente = Date.now() <= expiraMs;

    return { vigente, estado: rep.estado, expiraMs };
  };

  const formatearFecha = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  const colorCard = (estado) => {
    const v = String(estado || "").toLowerCase();
    if (v === "verde") return "rgba(39,174,96,.12)";
    if (v === "amarillo") return "rgba(241,196,15,.14)";
    if (v === "rojo") return "rgba(192,57,43,.12)";
    return "#ffffff";
  };

  const bordeCard = (estado, vigente) => {
    if (!vigente) return "1px dashed rgba(0,0,0,.20)";
    const v = String(estado || "").toLowerCase();
    if (v === "verde") return "1px solid rgba(39,174,96,.35)";
    if (v === "amarillo") return "1px solid rgba(241,196,15,.45)";
    if (v === "rojo") return "1px solid rgba(192,57,43,.35)";
    return "1px solid rgba(0,0,0,.10)";
  };

  // AUSENTE
  const bgAusente = "rgba(65,182,230,.10)";
  const borderAusente = "1px solid rgba(65,182,230,.25)";

  // =========================
  // Scroll sync (barra superior)
  // =========================
  const syncScrollTop = (e) => {
    if (!tableroRef.current) return;
    if (syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    tableroRef.current.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => (syncingScrollRef.current = false));
  };

  const syncScrollMain = (e) => {
    if (!scrollTopRef.current) return;
    if (syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    scrollTopRef.current.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => (syncingScrollRef.current = false));
  };

  if (loading) {
    return (
      <Box className="appContainer appContainer--wide">
        <Typography sx={{ color: "text.secondary" }}>Cargando panel…</Typography>
      </Box>
    );
  }

  return (
    <Box className="appContainer appContainer--wide">
      {/* Encabezado “estándar” (como el resto de pantallas) */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          SIL · Panel de Reportes
        </Typography>

        <Box sx={{ mt: 1.5, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Button startIcon={<RefreshIcon />} onClick={recargar} disabled={ausenciasLoading}>
            {ausenciasLoading ? "Actualizando…" : "Recargar"}
          </Button>

          {error ? (
            <Typography className="rojo" sx={{ fontWeight: 700 }}>
              {error}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {/* Scroll horizontal superior */}
      <Box
        ref={scrollTopRef}
        onScroll={syncScrollTop}
        sx={{
          height: 16,
          overflowX: "auto",
          overflowY: "hidden",
          borderRadius: 2,
          border: "1px solid rgba(0,0,0,.06)",
          background: "#fff",
          mb: 1,
        }}
      >
        <Box sx={{ width: GRID_WIDTH, height: 1 }} />
      </Box>

      {/* Canvas */}
      <Paper
        ref={tableroRef}
        onScroll={syncScrollMain}
        onMouseDown={onPanMouseDown}
        onMouseMove={onPanMouseMove}
        onMouseUp={stopPan}
        onMouseLeave={stopPan}
        sx={{
          height: "calc(100vh - 260px)",
          overflow: "auto",
          border: "1px solid rgba(0,0,0,.06)",
          borderRadius: 2,
          background: "#fff",
          cursor: isPanning ? "grabbing" : "grab",
          userSelect: "none",
        }}
      >
        <Box sx={{ width: GRID_WIDTH, height: GRID_HEIGHT }}>
          <GridLayout
            layout={layoutPanel}
            cols={COLS}
            rowHeight={ROW_HEIGHT}
            width={GRID_WIDTH}
            maxRows={FILAS}
            margin={[12, 12]}
            containerPadding={[12, 12]}
            isResizable={false}
            isDraggable={false}
            preventCollision
            compactType={null}
          >
            {nodosPanel.map((n) => {
              const rep = reportesMap.get(Number(n.id));
              const { vigente, estado, expiraMs } = getEstadoVigente(rep);

              const ausente = isNodoAusente(n.id);
              const ausencia = ausenciasMap.get(Number(n.id));

              const bg = ausente ? bgAusente : vigente ? colorCard(estado) : "#ffffff";
              const border = ausente ? borderAusente : bordeCard(estado, vigente);

              const usuarioAsignado = n?.usuario_id
                ? `${n.nombre || ""} ${n.apellidos || ""}`.trim()
                : "Sin asignar";

              const tooltipAusencia = ausente
                ? `AUSENTE desde: ${formatearFecha(ausencia?.desde)}${
                    ausencia?.motivo ? `\nMotivo: ${ausencia.motivo}` : ""
                  }`
                : "";

              const tooltipReporte = rep
                ? `Último: ${formatearFecha(rep.creado_en)}\nVálido hasta: ${
                    expiraMs ? new Date(expiraMs).toLocaleString() : "—"
                  }\nAutor: ${(`${rep.nombre || ""} ${rep.apellidos || ""}`.trim() || "—")}${
                    rep.comentario ? `\n\n${rep.comentario}` : ""
                  }`
                : "Sin reportes";

              const tooltip = [tooltipReporte, tooltipAusencia].filter(Boolean).join("\n\n");

              return (
                <Box key={String(n.id)} sx={{ cursor: "default" }}>
                  <Paper
                    elevation={0}
                    title={tooltip}
                    onClick={(e) => {
                      // Evitar clicks tras pan
                      if (panRef.current.moved) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    sx={{
                      height: "100%",
                      background: bg,
                      border,
                      borderRadius: 2,
                      p: 1.25,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      gap: 0.75,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, fontSize: 13, lineHeight: 1.1 }} noWrap>
                        {n.titulo}
                      </Typography>

                      <Typography sx={{ color: "text.secondary", fontSize: 12, mt: 0.5 }} noWrap>
                        {usuarioAsignado}
                        {n.etiqueta ? ` · ${n.etiqueta}` : ""}
                      </Typography>

                      {ausente ? (
                        <Box
                          sx={{
                            mt: 1,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1,
                            py: 0.5,
                            borderRadius: 999,
                            background: "rgba(65,182,230,.10)",
                            border: "1px solid rgba(65,182,230,.25)",
                            fontWeight: 800,
                            fontSize: 12,
                            width: "fit-content",
                          }}
                          title={tooltipAusencia}
                        >
                          <DirectionsWalkIcon sx={{ fontSize: 16 }} />
                          AUSENTE
                        </Box>
                      ) : null}
                    </Box>

                    {/* Fecha (no si AUSENTE) */}
                    {!ausente ? (
                      <Typography sx={{ fontSize: 12, color: "text.secondary", textAlign: "right" }}>
                        {rep?.creado_en ? formatearFecha(rep.creado_en) : "—"}
                      </Typography>
                    ) : (
                      <Box />
                    )}
                  </Paper>
                </Box>
              );
            })}
          </GridLayout>
        </Box>
      </Paper>
    </Box>
  );
};

export default PanelReportesSIL;
